import { Box, styled, Tab, Tabs, Typography } from '@mui/material'
import { TemplateSummaryOutputs } from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.types'
import { useState } from 'react'
import { TabPanel } from 'Common/Supplyvue-ui'
import { CycleSpeedGraph } from 'Features/Projects/Scenarios/Templates/TemplateResults/Results/CycleSpeedGraph'

type CycleSpeedProps = {
  data: CyclesGraphData
  optimization: 'constrained' | 'unconstrained'
}

type CyclesGraphData = {
  cycleByBatch: CycleSpeedData
  cycleByProduct: CycleSpeedData
}

type CycleSpeedData = {
  aggregatedData: AggregatedData[]
  averageCycleSpeed: number
}

type AggregatedData = {
  count: number
  label: number | string
  volume: number
}
const convertTemplateSummaryToCycleSpeedViewData = (
  data: TemplateSummaryOutputs[]
): CyclesGraphData => {
  const summary = data[0]

  const createAggregatedData = (
    label: number | string,
    count: number,
    volume: number
  ): AggregatedData => ({
    label,
    count,
    volume,
  })

  const cycleByProduct: CycleSpeedData = {
    aggregatedData: [
      createAggregatedData(
        '1',
        summary.Cycle1_CountSkuGroup,
        summary.Cycle1_SkuGroupVolume
      ),
      createAggregatedData(
        '2',
        summary.Cycle2_CountSkuGroup,
        summary.Cycle2_SkuGroupVolume
      ),
      createAggregatedData(
        '4',
        summary.Cycle4_CountSkuGroup,
        summary.Cycle4_SkuGroupVolume
      ),
      createAggregatedData(
        '8',
        summary.Cycle8_CountSkuGroup,
        summary.Cycle8_SkuGroupVolume
      ),
      createAggregatedData(
        'FOQ',
        summary.FOQ_CountSkuGroup,
        summary.FOQ_SkuGroupVolume
      ),
    ],
    averageCycleSpeed: parseFloat(summary.avgCycleSpeed.toFixed(2)),
  }

  const cycleByBatch: CycleSpeedData = {
    aggregatedData: [
      createAggregatedData(
        '1',
        summary.Cycle1_CountBatch,
        summary.Cycle1_BatchVolume
      ),
      createAggregatedData(
        '2',
        summary.Cycle2_CountBatch,
        summary.Cycle2_BatchVolume
      ),
      createAggregatedData(
        '4',
        summary.Cycle4_CountBatch,
        summary.Cycle4_BatchVolume
      ),
      createAggregatedData(
        '8',
        summary.Cycle8_CountBatch,
        summary.Cycle8_BatchVolume
      ),
      createAggregatedData(
        'FOQ',
        summary.FOQ_CountBatch,
        summary.FOQ_BatchVolume
      ),
    ],
    averageCycleSpeed: parseFloat(summary.avgCycleSpeed.toFixed(2)),
  }

  return {
    cycleByBatch,
    cycleByProduct,
  }
}

const OldCycleSpeedView = ({ data, optimization }: CycleSpeedProps) => {
  const [tabIndex, setTabIndex] = useState(0)
  const [productToggleButtonValue, setProductToggleButtonValue] =
    useState('count')
  const [batchToggleButtonValue, setBatchToggleButtonValue] = useState('count')

  const handleChange = (
    _event: React.ChangeEvent<unknown>,
    newValue: number
  ) => {
    setTabIndex(newValue)
  }

  const getProductXAxisData = () => {
    return data?.cycleByProduct?.aggregatedData.map(
      (data) =>
        data[productToggleButtonValue as keyof Omit<AggregatedData, 'label'>]
    )
  }

  const getBatchXAxisData = () => {
    return data?.cycleByBatch?.aggregatedData.map(
      (data) =>
        data[batchToggleButtonValue as keyof Omit<AggregatedData, 'label'>]
    )
  }

  const getYAxisData = (graphName: string) => {
    return graphName === 'cyclebyProduct'
      ? data?.cycleByProduct?.aggregatedData.map((data) =>
          data.label.toString()
        )
      : data?.cycleByBatch?.aggregatedData.map((data) => data.label.toString())
  }

  const CycleSpeedWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    alignSelf: 'center',
    paddingTop: '20px',
    paddingBottom: '40px',
    width: '140px',
    minWidth: '100px',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  }))

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-bewtween"
      alignItems="flex-start"
      width="100%"
      sx={{
        border: '1px solid lightgrey',
        marginTop: '8px',
      }}
    >
      <Tabs
        value={tabIndex}
        indicatorColor="primary"
        textColor="primary"
        onChange={handleChange}
        aria-label="Cycle Speed Tabs"
      >
        <Tab label="Cycle by SKU group" />
        <Tab label="Cycle by batch group" />
      </Tabs>
      <TabPanel value={tabIndex} index={0} padding={0}>
        <Box
          display="flex"
          flexDirection="row"
          gap="128px"
          justifyContent="space-between"
          sx={{
            flex: 1,
            width: '100%',
            paddingLeft: '16px',
            paddingBottom: '8px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flex: 1,
            }}
          >
            <CycleSpeedGraph
              xAxisHoverLabel={
                productToggleButtonValue === 'count' ? 'Count' : 'Volume'
              }
              xAxisData={getProductXAxisData()}
              yAxisData={getYAxisData('cyclebyProduct')}
            />
          </Box>
          <CycleSpeedWrapper>
            <Box
              mb={1}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <Typography variant="h3">Cycle speed</Typography>
              <Typography variant="body2">(Volume Weighted)</Typography>
            </Box>
            <Box>
              <Typography variant="h1">
                {data?.cycleByProduct?.averageCycleSpeed}
              </Typography>
            </Box>
          </CycleSpeedWrapper>
        </Box>
      </TabPanel>
      <TabPanel value={tabIndex} index={1} padding={0}>
        <Box
          display="flex"
          flexDirection="row"
          gap="128px"
          justifyContent="space-between"
          sx={{
            flex: 1,
            width: '100%',
            paddingLeft: '16px',
            paddingBottom: '8px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flex: 1,
            }}
          >
            <CycleSpeedGraph
              xAxisHoverLabel={
                batchToggleButtonValue === 'count' ? 'Count' : 'Volume'
              }
              xAxisData={getBatchXAxisData()}
              yAxisData={getYAxisData('cycleByBatch')}
            />
          </Box>
          <CycleSpeedWrapper>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              mb={1}
            >
              <Typography variant="h3">Cycle speed</Typography>
              <Typography variant="body2">(Volume Weighted)</Typography>
            </Box>
            <Box>
              <Typography variant="h1">
                {data?.cycleByBatch?.averageCycleSpeed}
              </Typography>
            </Box>
          </CycleSpeedWrapper>
        </Box>
      </TabPanel>
    </Box>
  )
}

export const CycleSpeedView = ({
  templateSummaryData,
}: {
  templateSummaryData: TemplateSummaryOutputs[]
}) => {
  const cycleSpeedData =
    convertTemplateSummaryToCycleSpeedViewData(templateSummaryData)

  return <OldCycleSpeedView data={cycleSpeedData} optimization="constrained" />
}
